"use client";

import React, { useEffect, useMemo, useState } from "react";
import { Check } from "lucide-react";

import { useDispatch, useSelector } from "react-redux";

import { cn } from "@/lib/utils";
import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import type { ICourse } from "@/api/admin/course";
import { handleDownload } from "@/utils/utils";
import type { RootState } from "@/store/store";
import { consumeBrochureDownload } from "@/store/slices/leaddrop";
import { resolveBrochureLink } from "@/lib/brochure";

/** Diagonal arrow — matches IIT lead thank-you / Figma CTA. */
function ThankYouCtaArrowIcon({ className }: { className?: string }) {
  return (
    <svg
      width={20}
      height={20}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn("shrink-0", className)}
      aria-hidden
    >
      <path
        d="M14.5908 5.16505L5.41168 14.8357M14.5908 5.16505L14.8058 13.4118M14.5908 5.16505L6.34404 5.38008"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export type LeadDrop7ThankYouProps = {
  /** Same right column as phone/OTP — no extra modal / close button */
  embedded?: boolean;
  variant: LEAD_DROP_VARIANTS | string;
  onClose?: () => void;
  brochureDownloadLink?: string;
  formValues: { interest?: string; course?: string };
  courses: ICourse[];
  coursePageErp?: ICourse | null;
  existingUserTitle?: string;
  existingUserTitleColor?: string;
  existingUserDescription?: string;
  existingUserDescriptionColor?: string;
  brochureTitle?: string;
  brochureTitleColor?: string;
  brochureDescription?: string;
  brochureDescriptionColor?: string;
  buttonColor?: string;
  buttonTextColor?: string;
  /** Primary CTA on thank-you; defaults to design copy, not “Sign up” */
  continueCtaLabel?: string;
  /** Countdown (seconds); 0 = disabled. When `applicationRedirectUrl` is set, timer ends with navigation there. */
  redirectSeconds?: number;
  /** Post-submit application path from API, e.g. `/application/{pok}?locale=in` */
  applicationRedirectUrl?: string;
  /** IIT Figma lead modal: mobile thank-you (ellipse, 18/16 type, 68px CTA, two-line default label). */
  figmaModalMobile?: boolean;
};


function resolveProgramLabel(
  courses: ICourse[],
  formValues: LeadDrop7ThankYouProps["formValues"],
  coursePageErp: ICourse | null | undefined,
): string {
  const erp = String(
    formValues.interest?.trim() ||
      formValues.course?.trim() ||
      coursePageErp?.erp_unicode ||
      "",
  );
  if (erp) {
    const match = courses.find((c) => c.erp_unicode === erp);
    const name = match?.program_name?.trim();
    if (name) return name;
  }
  const pageName = coursePageErp?.program_name?.trim();
  if (pageName) return pageName;
  if (erp) return erp;
  return "this programme";
}

const DEFAULT_CONTINUE = "Continue with Your Application";

/**
 * Cross-instance guard. The popup and the always-present enrollment card render
 * separate ThankYou instances; for a verified user both can reach the download
 * screen in the same commit. JS effects run sequentially, so the first to flip
 * this skips the rest — preventing a duplicate download for one request.
 */
let brochureDownloadInFlight = false;

/**
 * Thank-you / brochure success copy for Lead Drop 7.
 * With `embedded`, renders only the right-column body (same shell as OTP).
 */
const LeadDrop7ThankYou = ({
  embedded = false,
  variant,
  onClose,
  brochureDownloadLink,
  formValues,
  courses,
  coursePageErp,
  existingUserTitle = "",
  existingUserTitleColor = "",
  existingUserDescription = "",
  existingUserDescriptionColor = "",
  brochureTitle = "",
  brochureTitleColor = "",
  brochureDescription = "",
  brochureDescriptionColor = "",
  buttonColor,
  buttonTextColor,
  continueCtaLabel,
  redirectSeconds = 0,
  applicationRedirectUrl = "",
  figmaModalMobile = false,
}: LeadDrop7ThankYouProps) => {
  const dispatch = useDispatch();
  const isDownloadVariant =
    String(variant).toUpperCase() === LEAD_DROP_VARIANTS.DOWNLOAD;
  /** Only auto-download for an explicit, not-yet-delivered brochure request. */
  const pendingBrochureDownload = useSelector(
    (state: RootState) => state.leaddrop?.pendingBrochureDownload ?? false,
  );

  const appUrl = String(applicationRedirectUrl ?? "").trim();
  const effectiveRedirectSeconds =
    appUrl && (redirectSeconds ?? 0) > 0 ? (redirectSeconds ?? 0) : 0;

  const [secondsLeft, setSecondsLeft] = useState(0);
  /** Program Enrollments: no auto timer — starts after first CTA click (then shows countdown + redirects). */
  const [redirectSessionStarted, setRedirectSessionStarted] = useState(false);

  const deferEnrollmentCountdownUntilCta =
    embedded &&
    !figmaModalMobile &&
    effectiveRedirectSeconds > 0 &&
    Boolean(appUrl);

  const programLabel = useMemo(
    () => resolveProgramLabel(courses, formValues, coursePageErp ?? undefined),
    [courses, coursePageErp, formValues],
  );

  /** Course-mapped brochure for the registered course, falling back to the trigger's link. */
  const resolvedBrochureLink = useMemo(
    () =>
      resolveBrochureLink(
        courses,
        formValues,
        coursePageErp ?? undefined,
        brochureDownloadLink,
      ),
    [courses, formValues, coursePageErp, brochureDownloadLink],
  );

  useEffect(() => {
    if (!embedded || effectiveRedirectSeconds <= 0 || !appUrl) {
      setSecondsLeft(0);
      return;
    }

    if (deferEnrollmentCountdownUntilCta && !redirectSessionStarted) {
      setSecondsLeft(0);
      return;
    }

    let remaining = effectiveRedirectSeconds;
    setSecondsLeft(remaining);

    const id = window.setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        clearInterval(id);
        setSecondsLeft(0);
        window.location.assign(appUrl);
      } else {
        setSecondsLeft(remaining);
      }
    }, 1000);

    return () => clearInterval(id);
  }, [
    embedded,
    effectiveRedirectSeconds,
    appUrl,
    deferEnrollmentCountdownUntilCta,
    redirectSessionStarted,
  ]);

  const goContinue = () => {
    if (appUrl && typeof window !== "undefined") {
      if (deferEnrollmentCountdownUntilCta && !redirectSessionStarted) {
        setRedirectSessionStarted(true);
        return;
      }
      window.location.assign(appUrl);
      return;
    }
    onClose?.();
  };

  useEffect(() => {
    if (
      !isDownloadVariant ||
      !pendingBrochureDownload ||
      !resolvedBrochureLink ||
      brochureDownloadInFlight ||
      typeof window === "undefined"
    ) {
      return;
    }
    // Defer one macrotask. For an unmatched course the parent re-routes to the
    // registration step right after this child mounts, which unmounts this
    // screen and runs the cleanup below — cancelling the download. It only
    // fires when the thank-you screen stays mounted (registration complete or
    // the course was already matched).
    const timer = window.setTimeout(() => {
      brochureDownloadInFlight = true;
      // Consume the request so navigation (persisted DOWNLOAD variant) can't re-fire it.
      dispatch(consumeBrochureDownload());
      Promise.resolve(
        handleDownload(resolvedBrochureLink, "brochure.pdf"),
      ).finally(() => {
        brochureDownloadInFlight = false;
      });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [isDownloadVariant, pendingBrochureDownload, resolvedBrochureLink, dispatch]);

  const btnBg = (buttonColor && buttonColor.trim()) || "#db5803";
  const btnFg = (buttonTextColor && buttonTextColor.trim()) || "#ffffff";
  const ctaLabel =
    (continueCtaLabel && continueCtaLabel.trim()) || DEFAULT_CONTINUE;

  const fm = figmaModalMobile;

  /** Enrollments card: same thank-you UX as modal IIT, without mobile `lastscreen.svg` chrome */
  const embeddedEnrollmentThankYou = embedded && !fm;

  const useDefaultContinueLabel =
    !continueCtaLabel?.trim() ||
    continueCtaLabel.trim() === DEFAULT_CONTINUE;

  /** Two-line / `md` single-line CTA — IIT **modal** only (not Program Enrollments card) */
  const showResponsiveTwoLineContinue = useDefaultContinueLabel && fm;

  /** Modal popup: tall Figma CTA (68px mobile → 48px md) */
  const useModalIitCtaShell = fm;

  /** Program Enrollments embedded card: compact single-line CTA per design specs */
  const useEnrollmentCardCtaShell = embeddedEnrollmentThankYou;

  /** Single-line title + green program (matches popup); not when CMS overrides title */
  const useInlineProgramThankYouTitle =
    !existingUserTitle.trim() && (fm || embeddedEnrollmentThankYou);

  const wrapperClass = embedded
    ? cn(
        "flex w-full flex-col",
        fm &&
          "max-lg:relative max-lg:min-h-0 max-lg:flex-1 max-lg:overflow-visible max-lg:py-0 max-lg:pt-0 lg:min-h-0 lg:flex-1",
        fm
          ? "lg:gap-10 lg:pt-4 lg:pb-0"
          : embeddedEnrollmentThankYou
            ? "lg:gap-10 lg:py-4"
            : "gap-10 py-4",
      )
    : "flex min-h-[420px] w-full flex-col gap-10 rounded-xl bg-white p-6 sm:rounded-[20px]";

  /**
   * Download variant renders the same thank-you popup as the normal flow; the
   * brochure is fetched silently by the effect above. Brochure-specific CMS copy
   * still overrides the default thank-you text when configured.
   */
  const titleFromCms =
    existingUserTitle.trim() ||
    (isDownloadVariant ? brochureTitle.trim() : "");
  const bodyFromCms =
    existingUserDescription.trim() ||
    (isDownloadVariant ? brochureDescription.trim() : "");
  const titleColor =
    existingUserTitleColor || (isDownloadVariant ? brochureTitleColor : "");
  const descriptionColor =
    existingUserDescriptionColor ||
    (isDownloadVariant ? brochureDescriptionColor : "");

  const successMark = (
    <div
      className={cn(
        "relative z-[1] flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-[#00a571]",
        fm || embeddedEnrollmentThankYou
          ? "shadow-none"
          : "shadow-[0_8px_24px_rgba(0,0,0,0.15)]",
      )}
      aria-hidden
    >
      <Check
        className="h-11 w-11 text-white [filter:none]"
        strokeWidth={2.8}
      />
    </div>
  );

  const titleBlock =
    titleFromCms ? (
      <p
        className={cn(
          "font-poppins text-lg font-semibold leading-normal text-[#222] md:text-xl",
          fm && "max-lg:text-[18px]",
        )}
        style={titleColor !== "" ? { color: titleColor } : undefined}
      >
        {titleFromCms}
      </p>
    ) : useInlineProgramThankYouTitle ? (
      <p className="font-poppins max-w-full text-lg font-semibold leading-normal max-lg:text-[18px] md:text-xl">
        <span className="text-black max-lg:inline lg:text-[#222]">
          Thank you for showing your interest in{" "}
        </span>
        <span className="text-[#00a571]">{programLabel}</span>
      </p>
    ) : (
      <div className="flex flex-col gap-2 font-poppins font-semibold leading-snug">
        <p className="text-lg text-[#222] md:text-xl">
          Thank you for showing your interest in
        </p>
        <p className="text-lg text-[#00a571] md:text-xl">{programLabel}</p>
      </div>
    );

  const descriptionBlock = (
    <p
      className={cn(
        "font-poppins text-base font-normal leading-6 text-[#222]",
        (fm || embeddedEnrollmentThankYou) && "max-lg:text-[16px]",
      )}
      style={
        descriptionColor !== "" ? { color: descriptionColor } : undefined
      }
    >
      {bodyFromCms || "You can continue with your course application."}
    </p>
  );

  const ctaBlock =
    onClose || appUrl ? (
      <div
        className={cn(
          "flex w-full flex-col items-stretch gap-3",
          fm &&
            "max-lg:mx-auto max-lg:w-full max-lg:max-w-[min(340px,calc(100vw-3rem))] max-lg:shrink-0 lg:mx-auto lg:max-w-md",
          embeddedEnrollmentThankYou &&
            "mx-auto w-full max-w-[min(340px,calc(100%-1rem))] sm:max-w-md lg:mx-auto lg:max-w-md",
          !fm && !embeddedEnrollmentThankYou && "max-w-[22rem] sm:max-w-md",
        )}
      >
        <button
          type="button"
          onClick={goContinue}
          className={cn(
            "flex w-full shrink-0 items-center justify-center rounded-[4px] font-poppins font-medium text-white",
            !useModalIitCtaShell &&
              "shadow-[0px_20px_40px_0px_rgba(9,30,66,0.12)]",
            useModalIitCtaShell &&
              "h-[68px] gap-[25px] px-5 text-[16px] leading-[24px] md:h-[48px] md:px-6",
            useEnrollmentCardCtaShell &&
              "h-12 gap-[15px] px-4 text-[14px] leading-6 font-medium",
            !useModalIitCtaShell &&
              !useEnrollmentCardCtaShell &&
              "h-12 gap-[25px] px-6 text-[16px] leading-[24px]",
          )}
          style={{ backgroundColor: btnBg, color: btnFg }}
        >
          {showResponsiveTwoLineContinue ? (
            <>
              <span className="text-center">
                <span className="md:hidden">
                  Continue with Your
                  <br />
                  Application
                </span>
                <span className="hidden md:inline">{DEFAULT_CONTINUE}</span>
              </span>
              <ThankYouCtaArrowIcon className="text-current" />
            </>
          ) : (
            <>
              <span
                className={cn(
                  "text-center font-poppins font-medium",
                  useEnrollmentCardCtaShell
                    ? "min-w-0 text-[14px] leading-6"
                    : "text-[16px] leading-[24px]",
                  useModalIitCtaShell && "min-w-0",
                )}
              >
                {ctaLabel}
              </span>
              <ThankYouCtaArrowIcon className="text-current" />
            </>
          )}
        </button>

        {embedded &&
        effectiveRedirectSeconds > 0 &&
        (!deferEnrollmentCountdownUntilCta || redirectSessionStarted) ? (
          <p className="text-center font-poppins text-sm text-[#222]">
            Redirecting in{" "}
            <span className="font-semibold">{Math.max(0, secondsLeft)} Sec</span>
          </p>
        ) : null}
      </div>
    ) : null;

  return (
    <div
      className={cn(
        wrapperClass,
        fm || embeddedEnrollmentThankYou
          ? "max-lg:items-stretch lg:items-center lg:text-center"
          : "items-center text-center",
      )}
    >
      {fm ? (
        <div className="relative w-full shrink-0 overflow-visible lg:hidden">
          {/* Badge center on lastscreen.svg seam: ellipse bottom y=218.5 / viewBox height 307 */}
          <div className="relative w-full">
            <div className="overflow-hidden rounded-tr-[42px]">
              <img
                src="/svg/lastscreen.svg"
                alt=""
                width={500}
                height={307}
                className="block h-auto w-full"
                decoding="async"
              />
            </div>
            <div
              className="pointer-events-none absolute left-1/2 top-[calc(218.5/307*100%)] z-20 flex -translate-x-1/2 -translate-y-1/2 justify-center"
              aria-hidden
            >
              {successMark}
            </div>
          </div>
          <div className="relative z-10 bg-white px-5 pb-8 pt-8">
            <div
              className={cn(
                "relative z-[1] mx-auto flex w-full max-w-[min(340px,calc(100vw-2.5rem))] flex-col gap-[30px] text-center",
              )}
            >
              {titleBlock}
              {descriptionBlock}
            </div>
            <div className="relative z-[1] mt-8">{ctaBlock}</div>
          </div>
        </div>
      ) : null}

      <div
        className={cn(
          "relative z-[1] flex w-full flex-col items-center gap-10",
          fm &&
            "hidden min-h-0 lg:flex lg:flex-1 lg:justify-between lg:gap-10",
        )}
      >
        {successMark}
        <div
          className={cn(
            "relative z-[1] flex flex-col text-center",
            (fm || embeddedEnrollmentThankYou) && "lg:max-w-md lg:gap-[1.875rem]",
            !fm &&
              !embeddedEnrollmentThankYou &&
              "max-w-[22rem] gap-[1.875rem] sm:max-w-md",
          )}
        >
          {titleBlock}
          {descriptionBlock}
        </div>
        {ctaBlock}
      </div>
    </div>
  );
};

export default LeadDrop7ThankYou;
